import React, { createContext, useContext, useEffect, useState } from "react";
import { useCustomer } from "./CustomerContext";
import { useUserToken } from "./UserTokenContext";

const CreateTabContext = createContext();

export function CreateTabProvider({ children }) {
  /**
   * CustomerContext:
   * - customerDataFormPhone: aktif müşteri çağrı/form state’leri
   * - setDidUrl: müşteri bazlı DID / socket mapping
   */
  const { customerDataFormPhone, setCustomerDataFormPhone, setDidUrl } =
    useCustomer();

  /**
   * UserTokenContext:
   * - account değiştiğinde tüm tab ve localStorage state resetlenir
   */
  const { account } = useUserToken();

  /**
   * Açık olan tab’lerin listesi
   * - Normal sayfalar: customerID yok
   * - Müşteri tab’leri: customerID içerir
   */
  const [tabs, setTabs] = useState([
    {
      key: "maintab-1",
      title: "dashboard",
      path: "Dashboard/Dashboard",
    },
  ]);

  /**
   * UI tarafında tab.key üretmek için artan sayaç
   * Domain ile ilişkisi yok, sadece React key
   */
  const [nextTabKey, setNextTabKey] = useState(2);

  /** Aktif tab bilgileri */
  const [activeKey, setActiveKey] = useState("maintab-1");
  const [activePath, setActivePath] = useState("Dashboard/Dashboard");

  /**
   * new_customer tab’leri için sadece label amaçlı sayaç
   * Kimlik değil, sadece UI ismi
   */
  const [newCustomerCounter, setNewCustomerCounter] = useState(1);

  /**
   * Telefon numarasına göre HALİHAZIRDA açık olan
   * "new_customer" tab’ini bulur.
   *
   * Amaç:
   * - Aynı telefon numarası için ikinci bir yeni müşteri tab’i açılmasını engellemek
   * - localStorage + tabs state birlikte kontrol edilir
   */
  function findCustomerTabIndexByPhone(tabs, phoneNumber) {
    for (var i = 0; i < localStorage.length; i++) {
      var storageKey = localStorage.key(i);
      if (!storageKey || storageKey.indexOf("new_customer_") !== 0) continue;

      try {
        var value = JSON.parse(localStorage.getItem(storageKey));
        if (value && value.customerPhone === phoneNumber) {
          // tabs loop'u manuel
          for (var j = 0; j < tabs.length; j++) {
            if (tabs[j].customerID === storageKey) {
              return j;
            }
          }
        }
      } catch (e) {
        continue;
      }
    }

    return -1;
  }

  /**
   * Backend ID’si olmayan yeni müşteri için
   * geçici, localStorage ile eşleşen bir ID üretir
   */
  const generateTemporaryCustomerID = () => {
    return `new_customer_${Date.now()}`;
  };

  /** Yeni müşteri (telefonla gelen ama ID’si olmayan) senaryosu */
  const isNewCustomerByPhone = (isPhone, customerID) =>
    isPhone && customerID === null;

  /** Normal sayfa (dashboard vb.) senaryosu */
  const isNormalPage = (isPhone, customerID) => !isPhone && customerID === null;

  /** Backend’den gelen gerçek müşteri senaryosu */
  const isExistingCustomer = (customerID) => customerID !== null;

  /**
   * Açılmak istenen tab’in zaten var olup olmadığını belirler
   *
   * Kurallar:
   * - Yeni müşteri → telefon numarasına göre
   * - Normal sayfa → path’e göre
   * - Mevcut müşteri → customerID’ye göre
   */
  const findExistingTabIndex = ({
    tabs,
    isPhone,
    customerID,
    phoneNumber,
    path,
  }) => {
    if (isNewCustomerByPhone(isPhone, customerID)) {
      return findCustomerTabIndexByPhone(tabs, phoneNumber);
    }
    
    if (isNormalPage(isPhone, customerID)) {
      return tabs.findIndex((tab) => tab.path === path);
    }
    
    if (isExistingCustomer(customerID)) {
      return tabs.findIndex((tab) => tab.customerID === customerID);
    }

    return -1;
  };

  /**
   * Yeni tab açma veya var olan tab’i aktive etme fonksiyonu
   *
   * Aynı fonksiyon 3 farklı domain’i yönetir:
   * - Normal sayfalar
   * - Yeni müşteri (telefon bazlı)
   * - Mevcut müşteri (ID bazlı)
   */
  const addNewTab = React.useCallback((
    title,
    path,
    isPhone = false,
    customerID = null,
    phoneNumber = "",
  ) => {
    let actualTitle = title;

    // new_customer ise sadece UI label için counter eklenir
    if (title === "new_customer") {
      actualTitle = `new_customer-${newCustomerCounter}`;
      setNewCustomerCounter((prev) => prev + 1); // Counter'ı arttırıyoruz
    }

    const existingTabIndex = findExistingTabIndex({
      tabs,
      isPhone,
      customerID,
      phoneNumber,
      path,
    });

    // Tab zaten açıksa sadece ona geçilir
    if (existingTabIndex !== -1) {
      const existingTab = tabs[existingTabIndex];
      setActivePath(existingTab.path);
      setActiveKey(existingTab.key);
      return;
    }

    // Yeni tab oluşturma
    const newTabKey = `maintab-${nextTabKey}`;
    const resolvedCustomerID = customerID ?? generateTemporaryCustomerID();

    const isCustomerPath = path.startsWith("Crm/Customer/");

    const newTab = {
      key: newTabKey,
      title: actualTitle,
      path,
      ...((isPhone || isCustomerPath) && { customerID: resolvedCustomerID }),
    };

    setTabs((prev) => [...prev, newTab]);
    setNextTabKey((prev) => prev + 1);
    setActiveKey(newTabKey);
    setActivePath(path);
  }, [tabs, nextTabKey, newCustomerCounter]);

  /**
   * Tab kapatma işlemi
   *
   * Eğer müşteri tab’i ise:
   * - localStorage temizlenir
   * - socket / did / form state’leri kaldırılır
   */
  const removeTab = React.useCallback((tabKey) => {
    const tabToRemove = tabs.find((tab) => tab.key === tabKey);

    if (tabToRemove && tabToRemove.customerID) {
      const deleteScoketData = JSON.parse(
        localStorage.getItem(tabToRemove.customerID),
      );
      if (deleteScoketData?.customerDataFormPhone) {
        setCustomerDataFormPhone(
          customerDataFormPhone.filter(
            (item) =>
              item.callerid !==
              deleteScoketData.customerDataFormPhone[0]?.callerid,
          ),
        );
      }
      localStorage.removeItem(tabToRemove.customerID);
      setDidUrl((prev) => {
        const updated = { ...prev };
        delete updated[deleteScoketData?.customerPhone];
        return updated;
      });
    }

    const updatedTabs = tabs.filter((tab) => tab.key !== tabKey);
    setTabs(updatedTabs);

    // Tab kaldıysa ilk tab aktif edilir
    if (updatedTabs.length > 0) {
      setActivePath(updatedTabs[0].path);
      setActiveKey(updatedTabs[0].key);
    } else {
      // Eğer hiç tab kalmadıysa, aktif tab'ı null yapıyoruz
      setActiveKey(null);
    }
  }, [tabs, customerDataFormPhone, setCustomerDataFormPhone, setDidUrl]);

  /**
   * Kullanıcının manuel olarak tab değiştirmesi
   */
  const changeTab = React.useCallback((tabKey, tabPath) => {
    setActivePath(tabPath);
    setActiveKey(tabKey);
  }, []);

  /**
   * Tab başlığını günceller
   */
  const updateTabTitle = React.useCallback((tabKey, newTitle) => {
    setTabs((prev) =>
      prev.map((tab) =>
        tab.key === tabKey || tab.customerID === tabKey ? { ...tab, title: newTitle } : tab
      )
    );
  }, []);

  /**
   * Tab ID'sini günceller (new_customer_ -> customerid)
   */
  const updateTabId = React.useCallback((oldCustomerID, newCustomerID) => {
    setTabs((prev) =>
      prev.map((tab) =>
        tab.customerID === oldCustomerID
          ? { ...tab, customerID: String(newCustomerID) }
          : tab
      )
    );
  }, []);

  /**
   * Account değiştiğinde:
   * - CRM edit/add sayfaları kapatılır
   * - Tüm tab state resetlenir
   * - localStorage sadece gerekli anahtarlarla yeniden oluşturulur
   */
  useEffect(() => {
    if (
      activePath === "Crm/Customer/CustomerEdit" ||
      activePath === "Crm/Customer/CustomerAdd"
    ) {
      setActiveKey("maintab-1");
      setActivePath("Dashboard/Dashboard");
    }

    setTabs(
      tabs.filter(
        (tab) =>
          tab.path !== "Crm/Customer/CustomerEdit" &&
          tab.path !== "Crm/Customer/CustomerAdd",
      ),
    );

    const accountKey = localStorage.getItem("account");
    const activeChats = localStorage.getItem(`userActiveChats`);
    const chatHistory = localStorage.getItem("userChatHistory");
    localStorage.clear();
    localStorage.setItem("account", accountKey);
    activeChats !== null &&
      localStorage.setItem(`userActiveChats`, activeChats);
    chatHistory !== null &&
      localStorage.setItem(`userChatHistory`, chatHistory);
  }, [account]);

  const value = React.useMemo(() => ({
    tabs,
    addNewTab,
    removeTab,
    changeTab,
    updateTabTitle,
    updateTabId,
    activeKey,
    setActiveKey,
    activePath,
    setActivePath,
    setTabs,
    setNextTabKey,
  }), [tabs, addNewTab, removeTab, changeTab, updateTabTitle, updateTabId, activeKey, activePath]);

  return (
    <CreateTabContext.Provider value={value}>
      {children}
    </CreateTabContext.Provider>
  );
}

export function useCreateTab() {
  return useContext(CreateTabContext);
}
